import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Input from "../../../../components/FormInputs";
import AsynchronousDropdown from "../../../../components/AutoComplete";
import DatePicker from "../../../../components/Datepicker";
import SecureImage from "../../../../components/BucketImage/Image";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "../../../../service/validation/Assignvehicle";
import { assignvehicle, vehicle as vehicleSvg } from "../../../../common/svg";
import CustomDropDown from "../../../../components/Dropdown/CustomDropDown";
import { ApiService } from "../../../../service/api.service";
import { MASTER_URL, OPERTIONS_URL } from "../../../../common/common";
import toast from "react-hot-toast";

interface AssignVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripID: string;
  bookingID: string;
  onVehicleAdded?: () => void; // Callback to refresh data after vehicle is added
  existingVehicles?: any[]; // Existing assigned vehicles for edit mode
}

const AssignVehicleModal: React.FC<AssignVehicleModalProps> = ({
  isOpen,
  onClose,
  tripID,
  bookingID,
  onVehicleAdded,
  existingVehicles = [],
}) => {
  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      startTime: new Date().toISOString().slice(0, 16), // Current date and time
      endTime: new Date().toISOString().slice(0, 16), // Current date and time
      isImmediate: false,
    },
  });
  const apiService = new ApiService();
  const [vehicle, setVehicle] = React.useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = React.useState<string | number>("");
  const [assignedVehicles, setAssignedVehicles] = React.useState<any[]>([]);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState<any>(null);
  const [isLoadingVehicleDetails, setIsLoadingVehicleDetails] = useState(false);
  const apiServer = new ApiService();

  // Load existing vehicles when modal opens
  useEffect(() => {
    if (isOpen && existingVehicles.length > 0) {
      setAssignedVehicles(existingVehicles);
    }
  }, [isOpen, existingVehicles]);

  const onSubmit = async (data: any) => {
    try {
      if (assignedVehicles.length === 0) {
        toast.error("Please add at least one vehicle before saving");
        return;
      }

      const payload = {
        vehicleDetails: assignedVehicles
      };
      const response: any = await apiServer.postApi(`${OPERTIONS_URL}/vehicle/store-route`, payload);

      if (response.status === "200") {
        toast.success("Vehicle(s) assigned successfully!");
        reset(); // Reset form fields
        setAssignedVehicles([]); // Clear assigned vehicles
        setSelectedVehicleDetails(null); // Clear selected vehicle details
        onClose();
        // Call the refresh callback to update the parent component
        if (onVehicleAdded) {
          onVehicleAdded();
        }
      } else {
        toast.error("Failed to assign vehicle(s). Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error assigning vehicle(s). Please try again.");
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const fetchVehicles = async () => {
    try {
      const response: any = await apiService.getApi(`${MASTER_URL}/vehicle/get-route`);
      if (response.status === "200") {
        console.log("Vehicles fetched successfully:", response);
        const vehicles = response.body.data.map((vehicle: any) => ({
          label: vehicle.vehicleRegNo,
          value: vehicle.vehicleId,
          metaId: vehicle.id,
        }));
        setVehicle(vehicles);
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  const fetchVehicleDetails = async (vehicleId: string) => {
    if (!vehicleId) {
      setSelectedVehicleDetails(null);
      return;
    }

    try {
      setIsLoadingVehicleDetails(true);
      const response: any = await apiService.getApi(`${MASTER_URL}/vehicle/get-one-route?vehicleId=${vehicleId}`);
      if (response.status === "200") {
        setSelectedVehicleDetails(response.body);
      } else {
        setSelectedVehicleDetails(null);
      }
    } catch (error) {
      console.error("Error fetching vehicle details:", error);
      setSelectedVehicleDetails(null);
    } finally {
      setIsLoadingVehicleDetails(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const addVData = async () => {
    // Trigger validation
    const isValid = await trigger(["vehicleId", "task", "startTime", "endTime"]);

    if (!isValid) {
      console.warn("Form validation failed");
      return;
    }

    const formData = getValues();

    if (!formData.vehicleId) {
      console.warn("No vehicle selected");
      return;
    }

    const dataToStore = {
      vehicleId: formData.vehicleId,
      task: formData.task || "",
      startTime: formData.startTime || "",
      endTime: formData.endTime || "",
      isImmediate: formData.isImmediate || false,
      notes: formData.notes || "",
      bookingId: bookingID,
      tripId: tripID,
    };

    // Fetch vehicle details for the selected vehicle
    await fetchVehicleDetails(formData.vehicleId);

    console.log("Adding Vehicle Assignment:", dataToStore);
    setAssignedVehicles((prev) => [...prev, dataToStore]);

    // Reset form fields for next entry
    reset({
      vehicleId: "",
      task: "",
      startTime: new Date().toISOString().slice(0, 16), // Current date and time
      endTime: new Date().toISOString().slice(0, 16), // Current date and time
      isImmediate: false,
      notes: "",
    });
    setSelectedCompany("");
    toast.success("Vehicle added successfully!");
  };

  // Find vehicle label by ID for display purposes
  const getVehicleLabel = (vehicleId: string) => {
    const found = vehicle.find(v => v.value === vehicleId);
    return found ? found.label : vehicleId;
  };

  // Remove vehicle from assigned list
  const removeVehicle = (index: number) => {
    setAssignedVehicles(prev => prev.filter((_, i) => i !== index));
  };

  // Cleanup function to reset all state when modal closes
  const handleClose = () => {
    // Reset form
    reset({
      vehicleId: "",
      task: "",
      startTime: new Date().toISOString().slice(0, 16),
      endTime: new Date().toISOString().slice(0, 16),
      isImmediate: false,
      notes: "",
    });

    // Reset all state
    setSelectedCompany("");
    setAssignedVehicles([]);
    setSelectedVehicleDetails(null);
    setIsLoadingVehicleDetails(false);

    // Close modal
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
      <div className="bg-white rounded-lg shadow-lg w-[1000px] max-h-[90vh] overflow-auto flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-[18px] font-[600] font-poppins text-[#444FBD]">
            Assign Vehicle
          </h2>
          <button onClick={handleClose} className="text-gray-600 hover:text-black">
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-grow p-6 flex">
          {/* Left Side - Form */}
          <div className="w-1/2 pr-6">
            {/* Vehicle Selection */}
            <div className="ml-10">
              <div className="relative">
                <CustomDropDown
                  register={register}
                  name="vehicleId"
                  errors={errors}
                  options={vehicle || []}
                  label="Vehicle Number"
                  onChange={(value: any) => {
                    setSelectedCompany(value);
                    setValue("vehicleId", value);
                    // Automatically fetch vehicle details when vehicle is selected
                    fetchVehicleDetails(value);
                  }}
                />
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    className="text-[#444FBD] text-sm underline hover:text-blue-600"
                    onClick={() => {
                      // TODO: Implement add new vehicle functionality
                      toast("Add New Vehicle functionality will be implemented", {
                        icon: 'ℹ️',
                      });
                    }}
                  >
                    Add New Vehicle
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1 mt-2 mb-3 ml-10">
              <Input
                label="Task"
                name="task"
                errors={errors}
                register={register}
                placeholder="Enter Task Description"
                onBlur={() => trigger("task")}
              />
            </div>

            {/* Date Pickers */}
            <div className="mt-4 ml-10">
              <DatePicker
                label="Starting Date & Time"
                onDateChange={(date: any) => setValue("startTime", date)}
                register={register("startTime")}
                name="startTime"
                errors={errors}
                allowPastDates={true}
                includeTime={true}
                disabled={false}
              />
            </div>

            <div className="flex items-center mt-1 ml-10">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register("isImmediate")}
                  className="w-[14px] h-[14px]"
                />
                <span className="text-[#282828] text-[14px] font-normal">
                  Immediate
                </span>
              </label>
            </div>

            <div className="mt-4 ml-10">
              <DatePicker
                label="End Date & Time"
                onDateChange={(date: any) => setValue("endTime", date)}
                register={register("endTime")}
                name="endTime"
                errors={errors}
                allowPastDates={true}
                includeTime={true}
                disabled={false}
              />
            </div>


          </div>

          {/* Right Side - Vehicle Details or Illustration */}
          <div className="w-1/2 ml-6 flex flex-col border-[#C1C1C1] border-l border-dashed pl-6">
            {selectedVehicleDetails ? (
              <div className="w-full">
                {/* Availability Tag */}
                <div className="mb-4">
                  <span className="inline-block bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                    Available
                  </span>
                </div>

                {/* Main Content - Two Column Layout */}
                <div className="flex gap-8">
                  {/* Left Column - Vehicle Image and Specifications */}
                  <div className="w-2/5">
                    {/* Vehicle Image */}
                    <div className="mb-2">
                      <div className="w-full flex items-center justify-center">
                        <div className="text-center">
                          <SecureImage
                            fileKey={vehicleSvg}
                            alt="Vehicle"
                            width="120px"
                          />
                        </div>
                      </div>
                      <div className="text-center relative">
                        <p className="text-[#444FBD] font-medium inline-block">{selectedVehicleDetails.vehicleRegNo}</p>
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                    </div>

                    {/* Vehicle Specifications */}
                    <div className="mb-4">
                      <div className="space-y-0.5 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Size:</span>
                          <span className="text-gray-900">{selectedVehicleDetails.trailerSize || '20\''}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type:</span>
                          <span className="text-gray-900">{selectedVehicleDetails.vehicleType || 'Closed'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tyers:</span>
                          <span className="text-gray-900">{selectedVehicleDetails.tiresCount || '12'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Permit:</span>
                          <span className="text-gray-900">{selectedVehicleDetails.permitType || 'National'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Insurance:</span>
                          <span className="text-gray-900">{selectedVehicleDetails.insuranceStatus || 'Yes (Current)'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">RC:</span>
                          <span className="text-gray-900">{selectedVehicleDetails.rcNumber || '68t87tudtr8t898'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Owner/Executive Details and Notes */}
                  <div className="w-3/5">
                    {/* Owner/Executive Details */}
                    <div className="mb-4">
                      <div className="space-y-0.5 text-xs">
                        <div>
                          <span className="text-gray-600">Owner/Executive:</span>
                          <p className="text-[#444FBD] font-medium">{selectedVehicleDetails.ownerName || 'Praveen Elumalai'}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Company:</span>
                          <p className="text-[#444FBD] font-medium">{selectedVehicleDetails.companyName || 'Kittivaasal Technologies Pvt Ltd'}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Contact Number:</span>
                          <p className="text-gray-900">{selectedVehicleDetails.ownerContact || '+91 898687554378'}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Email:</span>
                          <p className="text-gray-900">{selectedVehicleDetails.ownerEmail || 'praveenfly22@gmail.com'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Notes Section */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                      <div className="text-sm text-gray-600">
                        {assignedVehicles.length > 0 && assignedVehicles[0]?.notes ? (
                          <p>{assignedVehicles[0].notes}</p>
                        ) : (
                          <p className="text-gray-400 italic">No notes added</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Field at Bottom */}
                <div className="mt-6">
                  <div className="flex flex-col gap-1">
                    <Input
                      label="Add Notes"
                      name="notes"
                      errors={errors}
                      register={register}
                      placeholder="Enter the notes........"
                      onBlur={() => trigger("notes")}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <SecureImage
                  fileKey={assignvehicle}
                  alt="Assign Vehicle"
                  width="296px"
                />
                <p className="text-sm text-gray-500 text-center mt-4">
                  Please assign vehicle for the task
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex gap-4 justify-end p-4 border-t">
          <button
            type="button"
            onClick={onSubmit}
            className="w-36 h-10 rounded-lg bg-[#444FBD] text-white transition duration-200 hover:bg-opacity-90"
            disabled={assignedVehicles.length === 0}
          >
            Save
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="w-36 h-10 rounded-lg bg-white text-gray-800 border border-gray-300 hover:border-gray-400 transition duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignVehicleModal;